import Auth from '@/api/clients/bingoManager-core/auth.js';
import CONST from '@/const/index.js'
import router from '@/router'
import { getTokenExpiration, isTokenExpired } from '@/utils/jwtHelper.js'

const getAuthDefaultState = () => {
    return {
        token: null,
        requiresPasswordReset: false,
        email: null,
        id: null,
        refreshToken: null,
        roles: [],
        userName: null,
        expiresAt: null,
        refreshTimer: null,
        isRefreshingToken: false,
        refreshPromise: null
    }
}

const initializeState = () => {
    const defaultState = getAuthDefaultState();
    try {
        const storedToken = localStorage.getItem("token");
        if (storedToken) {
            const parsedToken = JSON.parse(storedToken);
            if (parsedToken && typeof parsedToken === 'string' && parsedToken.split('.').length === 3) {
                defaultState.token = parsedToken;
            }
        }
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            const parsedUser = JSON.parse(storedUser);
            defaultState.email = parsedUser.email || null;
            defaultState.id = parsedUser.id || null;
            defaultState.refreshToken = parsedUser.refreshToken || null;
            defaultState.roles = parsedUser.roles || [];
            defaultState.userName = parsedUser.userName || null;
            defaultState.expiresAt = parsedUser.expiresAt || null;
            defaultState.requiresPasswordReset = parsedUser.passwordResetRequired || false;
        }
    } catch (e) {
        console.error('Error loading auth state from localStorage:', e);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    }
    return defaultState;
}

const readStoredAuth = () => {
    try {
        return {
            token: JSON.parse(localStorage.getItem('token')),
            user: JSON.parse(localStorage.getItem('user')) || {}
        };
    } catch (e) {
        return { token: null, user: {} };
    }
};

const isNewerValidToken = (storedToken, knownExpiresAt) => {
    if (typeof storedToken !== 'string' || isTokenExpired(storedToken)) {
        return false;
    }
    const storedExp = getTokenExpiration(storedToken);
    return !!storedExp && storedExp > (Number(knownExpiresAt) || 0);
};

const state = initializeState();

const getters = {
    getToken: (state) => state.token,
    getRequiresPasswordReset: state => state.requiresPasswordReset,
    getRefreshToken: state => state.refreshToken,
    getUserName: (state) => state.userName,
    getRoles: (state) => state.roles,
    isAuthenticated: (state) => !!state.token,
    getIsRefreshingToken: (state) => state.isRefreshingToken
}

const actions = {
    async login({ commit, dispatch }, user) {
        try {
            let response = await Auth.login(user);
            if (response.data.passwordResetRequired === true) {
                commit('updateRequiresPasswordReset', true);
            }
            if (typeof response.data.token === 'string' && response.data.token.length > 0) {
                localStorage.setItem('token', JSON.stringify(response.data.token));
                commit('updateToken', response.data.token);
            } else {
                throw new Error('Login response missing a valid string token');
            }
            const expiresAt = getTokenExpiration(response.data.token) || (Date.now() + 30 * 60 * 1000);
            if (!getTokenExpiration(response.data.token)) {
                console.warn("Token missing exp claim. Using 30m fallback.");
            }
            const userData = {
                email: response.data.email,
                id: response.data.id,
                refreshToken: response.data.refreshToken,
                roles: response.data.roles,
                userName: response.data.userName,
                expiresAt: expiresAt,
                passwordResetRequired: response.data.passwordResetRequired || false
            };
            localStorage.setItem('user', JSON.stringify(userData));
            commit('updateUserInfo', userData);
            dispatch('setupRefreshTimer');
        } catch (err) {
            console.error('Login error:', err);
            this._vm.$snackbar({
                message: ("Oops, somethings gone wrong. Please try again"),
                mode: CONST.THEME.COLOURS.ERROR
            }, dispatch);
            throw err;
        }
    },

    async refreshToken({ commit, state, dispatch }) {
        if (state.isRefreshingToken) {
            return state.refreshPromise;
        }
        const stored = readStoredAuth();
        if (isNewerValidToken(stored.token, state.expiresAt)) {
            commit('updateToken', stored.token);
            if (stored.user.refreshToken) {
                commit('updateRefreshToken', stored.user.refreshToken);
            }
            commit('updateExpiresAt', getTokenExpiration(stored.token));
            dispatch('setupRefreshTimer');
            return stored.token;
        }
        const activeRefreshToken = stored.user.refreshToken || state.refreshToken;
        if (!activeRefreshToken) {
            throw new Error('No refresh token available');
        }
        commit('setIsRefreshingToken', true);
        const refreshPromise = Auth.refreshToken(activeRefreshToken)
            .then(response => {
                const token = response.data.token || response.data.accessToken;
                if (typeof token === 'string' && token.length > 0) {
                    localStorage.setItem('token', JSON.stringify(token));
                    commit('updateToken', token);
                    const userData = JSON.parse(localStorage.getItem('user') || '{}');
                    if (response.data.refreshToken) {
                        userData.refreshToken = response.data.refreshToken;
                        commit('updateRefreshToken', response.data.refreshToken);
                    }
                    const expiresAt = getTokenExpiration(token) || (Date.now() + 30 * 60 * 1000);
                    if (!getTokenExpiration(token)) {
                        console.warn("Token missing exp claim. Using 30m fallback.");
                    }
                    userData.expiresAt = expiresAt;
                    commit('updateExpiresAt', expiresAt);
                    localStorage.setItem('user', JSON.stringify(userData));
                    dispatch('setupRefreshTimer');
                    return token;
                }
                throw new Error('No token in refresh response');
            })
            .catch(error => {
                const latest = readStoredAuth();
                if (isNewerValidToken(latest.token, state.expiresAt)) {
                    commit('updateToken', latest.token);
                    if (latest.user.refreshToken) {
                        commit('updateRefreshToken', latest.user.refreshToken);
                    }
                    commit('updateExpiresAt', getTokenExpiration(latest.token));
                    dispatch('setupRefreshTimer');
                    return latest.token;
                }
                dispatch('logout');
                throw error;
            })
            .finally(() => {
                commit('setIsRefreshingToken', false);
                commit('setRefreshPromise', null);
            });
        commit('setRefreshPromise', refreshPromise);
        return refreshPromise;
    },

    setupRefreshTimer({ state, commit, dispatch }) {
        if (state.refreshTimer) {
            clearTimeout(state.refreshTimer);
            commit('setRefreshTimer', null);
        }
        if (!state.expiresAt || !state.refreshToken) return;
        const now = Date.now();
        const expiresAt = isNaN(state.expiresAt) ? new Date(state.expiresAt).getTime() : Number(state.expiresAt);
        if (isNaN(expiresAt)) {
            console.error("Invalid expiresAt format:", state.expiresAt);
            return;
        }
        const buffer = 2 * 60 * 1000;
        let timeUntilRefresh = (expiresAt - now) - buffer;
        if (timeUntilRefresh < 30000) {
            console.warn("Token expires very soon or is already expired. Waiting 30s before next refresh attempt.");
            timeUntilRefresh = 30000;
        }
        timeUntilRefresh += Math.floor(Math.random() * 30000);
        const timer = setTimeout(() => {
            dispatch('refreshToken').catch(err => {
                console.error("Background refresh failed:", err);
            });
        }, timeUntilRefresh);
        commit('setRefreshTimer', timer);
    },

    async forgotPassword({ dispatch }, userName) {
        try {
            let response = await Auth.forgotPassword(userName);
            if (response.data) {
                this._vm.$snackbar({
                    message: ("If this username exists, a password reset mail will be sent to the related email address"),
                    mode: CONST.THEME.COLOURS.WARNING
                }, dispatch);
            }
        } catch (err) {
            this._vm.$snackbar({
                message: ("Oops, somethings gone wrong. Please try again"),
                mode: CONST.THEME.COLOURS.ERROR
            }, dispatch);
        }
    },
    async changePassword({ commit, dispatch }, passwordDetails) {
        try {
            let response = await Auth.changePassword(passwordDetails);
            if (response.data) {
                commit('updateRequiresPasswordReset', false);
                const userData = JSON.parse(localStorage.getItem('user') || '{}');
                userData.passwordResetRequired = false;
                localStorage.setItem('user', JSON.stringify(userData));
                this._vm.$snackbar({
                    message: ("Your password has been changed successfully"),
                    mode: CONST.THEME.COLOURS.SUCCESS
                }, dispatch);
                router.push("/");
            }
        } catch (err) {
            this._vm.$snackbar({
                message: ("Oops, somethings gone wrong. Please try again"),
                mode: CONST.THEME.COLOURS.ERROR
            }, dispatch);
        }
    },
    async resetPassword({ dispatch }, passwordDetails) {
        try {
            let response = await Auth.resetPassword(passwordDetails);
            if (response.data) {
                this._vm.$snackbar({
                    message: ("Your password has been changed successfully"),
                    mode: CONST.THEME.COLOURS.SUCCESS
                }, dispatch);
            }
        } catch (err) {
            this._vm.$snackbar({
                message: ("Oops, somethings gone wrong. Please try again"),
                mode: CONST.THEME.COLOURS.ERROR
            }, dispatch);
        }
    },
    async logout({ commit, state }) {
        try {
            if (state.refreshToken) {
                await Auth.logout(state.refreshToken);
            }
        } catch (e) {
            console.error('Logout revoke failed:', e);
        }
        commit("resetAuth");
        commit("resetSchedule");
        commit("resetBooks");
        commit("resetCaller");
        commit("resetCustomer");
        commit("resetProducts");
        commit("resetUsers");
        commit("resetTemplates");
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        if (state.refreshTimer) {
            clearTimeout(state.refreshTimer);
            commit('setRefreshTimer', null);
        }
        router.push("/login");
    }
}

const mutations = {
    updateToken(state, token) {
        state.token = token;
    },
    updateRequiresPasswordReset(state, required) {
        state.requiresPasswordReset = required;
    },
    updateUserInfo(state, data) {
        state.email = data.email;
        state.id = data.id;
        state.refreshToken = data.refreshToken;
        state.roles = data.roles;
        state.userName = data.userName;
        state.expiresAt = data.expiresAt;
        state.requiresPasswordReset = data.passwordResetRequired || false;
    },
    updateRefreshToken(state, refreshToken) {
        state.refreshToken = refreshToken;
    },
    updateExpiresAt(state, expiresAt) {
        state.expiresAt = expiresAt;
    },
    setRefreshTimer(state, timer) {
        state.refreshTimer = timer;
    },
    setIsRefreshingToken(state, isRefreshing) {
        state.isRefreshingToken = isRefreshing;
    },
    setRefreshPromise(state, promise) {
        state.refreshPromise = promise;
    },
    resetAuth(state) {
        const defaultState = getAuthDefaultState();
        Object.keys(defaultState).forEach(key => {
            state[key] = defaultState[key];
        });
    }
}

export default {
    state,
    getters,
    actions,
    mutations
}
